/**
 * Shared SSO-session detection for the frontend (ENT-3463).
 *
 * An "SSO session" is one whose access-token `amr` (authentication method
 * references) records an IdP assertion — `sso/saml`. Detection keys on the
 * SESSION's own authentication method, NEVER identity ownership: a break-glass
 * PASSWORD session of an SSO-created account has a password/totp `amr` and must
 * read as NOT an SSO session (spec decision D1 carve-out). `app_metadata.provider`
 * is identity-level and is deliberately NOT consulted here.
 *
 * Mirror of `supabase/functions/_shared/sso-session.ts`; the two runtimes cannot
 * share code.
 */

import { decodeJwt } from 'jose';

export interface SsoSessionLike {
  access_token?: string | null;
}

const SSO_METHOD_SUBSTRINGS = ['sso', 'saml'] as const;

/** True when any `amr` entry's method references an SSO/SAML assertion. */
export function isSsoAmr(amr: unknown): boolean {
  if (!Array.isArray(amr)) return false;
  return amr.some((raw) => {
    if (!raw || typeof raw !== 'object') return false;
    const method = (raw as { method?: unknown }).method;
    if (typeof method !== 'string') return false;
    const lowered = method.toLowerCase();
    return SSO_METHOD_SUBSTRINGS.some((needle) => lowered.includes(needle));
  });
}

/**
 * Report whether a Supabase session is IdP-authenticated (SSO).
 *
 * The access token is decoded with `jose`'s `decodeJwt` — the repo-approved
 * library primitive for the standardized JWT wire format (no bespoke
 * base64url/JSON parser; ENT-2969 tripwire). `decodeJwt` reads the claims
 * WITHOUT signature verification, which is exactly right here: the session is
 * already an authenticated GoTrue session, so we only inspect its `amr` to
 * classify how it was authenticated — we are not authenticating the token.
 */
export function isSsoSession(session: SsoSessionLike | null | undefined): boolean {
  const token = session?.access_token;
  if (typeof token !== 'string' || token.length === 0) return false;
  let claims: Record<string, unknown>;
  try {
    claims = decodeJwt(token);
  } catch {
    return false;
  }
  return isSsoAmr((claims as { amr?: unknown }).amr);
}

/**
 * ENT-4204: epoch-seconds timestamp of the most recent `sso/saml` amr assertion
 * in the session's access token, or null when there is none. This is the
 * ground-truth freshness signal for a step-up re-assertion: a NEW
 * `signInWithSSO` mints a session whose `sso/saml` amr is timestamped "now",
 * whereas a background token REFRESH carries the ORIGINAL assertion's amr
 * timestamp forward unchanged. Comparing this value before vs after the IdP
 * round-trip therefore proves a genuinely fresh assertion — unlike `expires_at`,
 * which a refresh also advances (Codex round-7 P2).
 */
export function latestSsoAssertionAtSeconds(
  session: SsoSessionLike | null | undefined,
): number | null {
  const token = session?.access_token;
  if (typeof token !== 'string' || token.length === 0) return null;
  let claims: Record<string, unknown>;
  try {
    claims = decodeJwt(token);
  } catch {
    return null;
  }
  const amr = (claims as { amr?: unknown }).amr;
  if (!Array.isArray(amr)) return null;
  let latest: number | null = null;
  for (const raw of amr) {
    if (!raw || typeof raw !== 'object') continue;
    const method = (raw as { method?: unknown }).method;
    if (typeof method !== 'string') continue;
    const lowered = method.toLowerCase();
    if (!SSO_METHOD_SUBSTRINGS.some((needle) => lowered.includes(needle))) continue;
    const ts = (raw as { timestamp?: unknown }).timestamp;
    if (typeof ts === 'number' && Number.isFinite(ts) && (latest === null || ts > latest)) {
      latest = ts;
    }
  }
  return latest;
}
